import { useEffect, useState } from 'react';
import { Alert, Button, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import { supabase } from '../config/supabase';

type PilotPlayer = { id: string; name: string };
type Job = { id: string; status: 'pending' | 'leased' | 'completed' | 'failed'; errorClassification?: string;
  result?: { changed: boolean; newEdr: number | null; errorClassification?: string } };

async function request(path: string, body?: unknown) {
  const { data } = await supabase.auth.getSession();
  if (!data.session) throw new Error('Please sign in again.');
  const response = await fetch(`/api/automation/${path}`, {
    method: body ? 'POST' : 'GET', headers: { Authorization: `Bearer ${data.session.access_token}`, 'Content-Type': 'application/json' },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  if (!response.ok) throw new Error('The staging pilot request could not be completed.');
  return response.json();
}

// Removable Phase 2 validation panel. Does not replace or invoke existing Run Now.
export default function TennisRecordAutomationPilot() {
  const [players, setPlayers] = useState<PilotPlayer[]>([]);
  const [playerId, setPlayerId] = useState('');
  const [job, setJob] = useState<Job | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [requestKey, setRequestKey] = useState(() => crypto.randomUUID());
  useEffect(() => {
    if (import.meta.env.COURTCAPTAIN_ENV !== 'staging') return;
    let active = true;
    void request('pilot').then((data: { players: PilotPlayer[] }) => { if (active) setPlayers(data.players); }).catch(() => {});
    return () => { active = false; };
  }, []);
  useEffect(() => {
    if (!job || !['pending', 'leased'].includes(job.status)) return;
    let active = true;
    const interval = window.setInterval(() => {
      void request(`jobs/${job.id}`).then((next: Job) => { if (active) { setJob(next); setError(''); } })
        .catch(() => { if (active) setError('Status is temporarily unavailable. The job remains durable.'); });
    }, 3000);
    return () => { active = false; window.clearInterval(interval); };
  }, [job?.id, job?.status]);
  if (!players.length) return null;
  const running = busy || (job !== null && ['pending', 'leased'].includes(job.status));
  const queue = async () => {
    setBusy(true); setError('');
    try {
      setJob(await request('player-refresh', { playerId, requestKey }));
      setRequestKey(crypto.randomUUID());
    } catch { setError('Unable to queue the refresh. Retrying will use the same request key.'); }
    finally { setBusy(false); }
  };
  return <Paper variant="outlined" sx={{ p: 2 }}><Stack spacing={1.5}>
    <Typography variant="subtitle1">Staging single-player refresh pilot</Typography>
    <TextField select label="Player" value={playerId} disabled={running} onChange={(event) => { setPlayerId(event.target.value); setRequestKey(crypto.randomUUID()); }}>
      {players.map((player) => <MenuItem key={player.id} value={player.id}>{player.name}</MenuItem>)}
    </TextField>
    <Button onClick={() => void queue()} disabled={!playerId || running}>Queue one refresh</Button>
    {job && <Typography variant="body2">Status: {job.status === 'pending' ? 'queued' : job.status}</Typography>}
    {job?.status === 'completed' && <Alert severity="success">{job.result?.changed ? 'EDR updated' : 'EDR unchanged'}: {job.result?.newEdr ?? 'unrated'}</Alert>}
    {job?.status === 'failed' && <Alert severity="warning">Refresh failed ({job.errorClassification || 'acquisition_failed'}). Existing EDR preserved.</Alert>}
    {error && <Alert severity="error">{error}</Alert>}
  </Stack></Paper>;
}
